import React, { FC, createContext, useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router';
import { LoginDataType, PropertyType, DASHBOARD_URL } from '../../common';
import { api, bov3_api } from '../../api';

let logOutTimer: ReturnType<typeof setTimeout>;

interface AuthContextType {
	user?: PropertyType | null;
	uid: string;
	token: string;
	loginType: string;
	isLoggedIn: boolean;
	setUser: (data: PropertyType) => void;
	setLoginType: (data: string) => void;
	login: (data: LoginDataType) => void;
	logout: () => void;
}

const AuthContext = createContext<AuthContextType>({
	uid: '',
	token: '',
	isLoggedIn: false,
	loginType: '',
	setUser: () => {},
	setLoginType: () => {},
	login: () => {},
	logout: () => {},
});

interface AuthProps {
	children: JSX.Element;
}

const storeLogin = (data: LoginDataType) => {
	localStorage.setItem('user_data', JSON.stringify(data));
};

const clearStorage = () => {
	localStorage.removeItem('user_data');
};

const remainingTime = (exp?: string) => {
	if (!exp || typeof exp !== 'string') return 0;
	const current = new Date().getTime();
	const expTime = new Date(exp.includes('Z') || exp.includes('+') ? exp : `${exp} UTC`).getTime();
	return isNaN(expTime) ? 0 : expTime - current;
};

const getInitials = () => {
	const data = localStorage.getItem('user_data');
	const inData = data && JSON.parse(data);

	if (!inData || remainingTime(inData.tokenExpiration) <= 0) {
		clearStorage();
		return;
	}
	return inData;
};

const INACTIVITY_TIMEOUT = 60 * 60 * 1000; // 1 hour

const updateStoredExpiration = () => {
	const data = localStorage.getItem('user_data');
	if (data) {
		try {
			const parsed = JSON.parse(data);
			parsed.tokenExpiration = new Date(Date.now() + INACTIVITY_TIMEOUT).toISOString();
			localStorage.setItem('user_data', JSON.stringify(parsed));
		} catch {
			// ignore parse error
		}
	}
};

const AuthContextProvider: FC<AuthProps> = ({ children }): JSX.Element => {
	const inData = getInitials();
	const [user, setUser] = useState<PropertyType | null>(null);
	const [uid, setUid] = useState(inData ? inData.uid : null);
	const [token, setToken] = useState(inData ? inData.token : '');
	const [inactivityTimeout, setInactivityTimeout] = useState(
		inData ? remainingTime(inData.tokenExpiration) : INACTIVITY_TIMEOUT
	);
	const [loginType, setLoginType] = useState('');
	const history = useHistory();

	const onLogOut = useCallback(() => {
		setToken('');
		setUser(null);
		clearStorage();
		if (logOutTimer) {
			clearTimeout(logOutTimer);
		}
		history.replace('/login');
	}, [history]);

	const resetLogOutTimer = useCallback(
		(timeout = INACTIVITY_TIMEOUT) => {
			if (logOutTimer) {
				clearTimeout(logOutTimer);
			}
			if (token) {
				logOutTimer = setTimeout(onLogOut, timeout);
			}
		},
		[onLogOut, token]
	);

	const extendLogOutTimer = useCallback(() => {
		if (logOutTimer) {
			clearTimeout(logOutTimer);
		}
		if (token) {
			logOutTimer = setTimeout(onLogOut, INACTIVITY_TIMEOUT);
		}
		updateStoredExpiration();
	}, [onLogOut, token]);

	const onLogIn = (data: LoginDataType) => {
		const timeout = remainingTime(data.tokenExpiration) || INACTIVITY_TIMEOUT;
		setUid(data.uid);
		setToken(data.token);
		setInactivityTimeout(timeout);
		storeLogin(data);

		if (logOutTimer) {
			clearTimeout(logOutTimer);
		}
		logOutTimer = setTimeout(onLogOut, timeout);
		history.replace(DASHBOARD_URL.value);
	};

	useEffect(() => {
		if (token) {
			resetLogOutTimer(inactivityTimeout);

			const interceptor = api.interceptors.request.use((config) => {
				extendLogOutTimer();
				return config;
			});

			const bov3Interceptor = bov3_api.interceptors.request.use((config) => {
				extendLogOutTimer();
				return config;
			});

			const responseInterceptor = api.interceptors.response.use(
				(request) => request,
				(error) => {
					const url = error?.response?.config?.url;
					if (error?.response?.status === 401 && !(url.includes('login') || url.includes('reset'))) {
						onLogOut();
					}
					return Promise.reject({ ...error });
				}
			);

			return () => {
				api.interceptors.request.eject(interceptor);
				bov3_api.interceptors.request.eject(bov3Interceptor);
				api.interceptors.response.eject(responseInterceptor);
				if (logOutTimer) {
					clearTimeout(logOutTimer);
				}
			};
		}
	}, [token, inactivityTimeout, onLogOut, resetLogOutTimer, extendLogOutTimer]);

	const value = {
		user: user,
		uid: uid,
		token: token,
		loginType: loginType,
		isLoggedIn: !!token,
		setUser: setUser,
		login: onLogIn,
		logout: onLogOut,
		setLoginType: setLoginType,
	};

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export { AuthContext, AuthContextProvider };
